import type { VoidComponent } from "solid-js";
import { createMemo, Match, mergeProps, Show, Switch } from "solid-js";
import Text from "../../../../components/text/Text.tsx";
import { resolveFullName } from "../../../../utils/userUtil.ts";
import { isNil } from "lodash-es";
import { useTranslation } from "../../../../state/i18n.tsx";
import { TextButton } from "../../../../components/button/Button.tsx";
import { formatDate } from "../../../../components/formatting/FormatDate.tsx";
import { RadioGroup } from "@ark-ui/solid";
import type FormVersionId from "../../../../state/FormVersionId.ts";
import { IconOnTrack, TrackDot, VersionHistoryTrackStartIcon } from "./VersionHistorySidebarTrack.tsx";
import type { HistoryItemAuthor } from "./VersionHistorySidebar.tsx";
import { Icon } from "../../../../components/icon/Icon.tsx";

import { MemberAvatar } from "../../../../components/avatar/Avatar.tsx";

export type VersionHistorySidebarItemType =
  | "savedChanges"
  | "unsavedChanges"
  | "currentlyPublished"
  | "previouslyPublished";

export const discardableItemTypes: VersionHistorySidebarItemType[] = ["unsavedChanges"];
export const restorableItemTypes: VersionHistorySidebarItemType[] = ["currentlyPublished", "previouslyPublished"];

type VersionHistorySidebarItemProps = {
  type: VersionHistorySidebarItemType;
  formVersionId: FormVersionId;
  lastUpdatedAt: Date;
  author?: HistoryItemAuthor;
  isFirstItem?: boolean;
  isLastItem?: boolean;
  selected: boolean;
  onClick?: () => void;
};

const VersionHistorySidebarItem: VoidComponent<VersionHistorySidebarItemProps> = (rawProps) => {
  const props = mergeProps({ isFirstItem: false, isLastItem: false }, rawProps);

  const { t } = useTranslation();
  const title = createMemo(() => {
    switch (props.type) {
      case "savedChanges":
        return t(($) => $.VERSION_HISTORY_SIDEBAR_ITEM_SAVED_CHANGES);
      case "unsavedChanges":
        return t(($) => $.VERSION_HISTORY_SIDEBAR_ITEM_UNSAVED_CHANGES);
      case "currentlyPublished":
        return t(($) => $.VERSION_HISTORY_SIDEBAR_ITEM_CURRENTLY_PUBLISHED);
      case "previouslyPublished":
        return t(($) => $.VERSION_HISTORY_SIDEBAR_ITEM_PREVIOUSLY_PUBLISHED);
    }
  });

  const authorName = createMemo(() => {
    const author = props.author;
    if (isNil(author)) {
      return undefined;
    }
    if (author.type === "apiUser") {
      return isNil(author.name)
        ? t(($) => $.VERSION_HISTORY_SIDEBAR_ITEM_API_USER)
        : t(($) => $.VERSION_HISTORY_SIDEBAR_ITEM_API_USER_NAMED, { name: author.name });
    }
    return resolveFullName(author.user);
  });

  const isDiscardable = createMemo(() => discardableItemTypes.includes(props.type));
  const isRestorable = createMemo(() => restorableItemTypes.includes(props.type));

  return (
    <RadioGroup.Item
      class="group flex w-full flex-row items-stretch gap-1.5 rounded-sm"
      value={props.formVersionId.toString()}
    >
      <RadioGroup.ItemHiddenInput />
      <div class="flex max-w-8 min-w-8 flex-col items-center">
        <IconOnTrack head={!props.isFirstItem} tail={!props.isLastItem}>
          <Switch fallback={<TrackDot color="white" />}>
            <Match when={props.isFirstItem}>
              <VersionHistoryTrackStartIcon selected={props.selected} />
            </Match>
            <Match when={props.type === "currentlyPublished"}>
              <TrackDot color="green" />
            </Match>
            <Match when={props.selected}>
              <TrackDot color="blue" />
            </Match>
          </Switch>
        </IconOnTrack>
      </div>
      <div class="min-w-0 grow pb-3">
        <div
          // oxlint-disable-next-line better-tailwindcss/no-restricted-classes
          class="flex flex-col items-start gap-2 rounded-sm p-2 group-has-[input:focus-visible]:outline-default"
          classList={{ "bg-surface-active-brand": props.selected, "hover:bg-surface-hover": !props.selected }}
        >
          <Text class="text-base-semibold text-primary" text={title()} />
          <Text class="text-base-regular text-primary" text={formatDate(props.lastUpdatedAt)} />
          <Show when={authorName()}>
            {(name) => (
              <div class="flex w-full flex-row items-center gap-2">
                <Switch>
                  <Match when={props.author?.type === "user" && props.author}>
                    {(author) => (
                      <>
                        <MemberAvatar member={author().user} size="xs" />
                        <Text
                          class="min-w-0 text-base-regular text-primary"
                          text={t(($) => $.VERSION_HISTORY_SIDEBAR_ITEM_BY, {
                            name: name(),
                          })}
                          truncate
                        />
                      </>
                    )}
                  </Match>
                  <Match when={props.author?.type === "apiUser"}>
                    <>
                      <div class="rounded-full bg-surface-subtle p-1.25">
                        <Icon name="cloud" type="outline" size="sm" class="icon-secondary" />
                      </div>
                      <Text class="text-base-regular text-primary" text={name()} truncate />
                    </>
                  </Match>
                </Switch>
              </div>
            )}
          </Show>
          <Show when={props.selected}>
            <Switch>
              <Match when={isDiscardable()}>
                <TextButton
                  class="mt-2"
                  text={t(($) => $.VERSION_HISTORY_SIDEBAR_ITEM_DISCARD_CHANGES)}
                  size="medium"
                  variant="secondary"
                  color="destructive"
                  onClick={props.onClick}
                />
              </Match>
              <Match when={isRestorable()}>
                <TextButton
                  class="mt-2"
                  text={t(($) => $.VERSION_HISTORY_SIDEBAR_ITEM_RESTORE_AS_DRAFT)}
                  size="medium"
                  variant="secondary"
                  onClick={props.onClick}
                />
              </Match>
            </Switch>
          </Show>
        </div>
      </div>
    </RadioGroup.Item>
  );
};

export default VersionHistorySidebarItem;
